import { useState } from "react";
import { Link } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  GraduationCap, Heart, ArrowRight, ArrowLeft, FileText, Database, Users, CheckCircle2,
} from "lucide-react";

const jobseekerOptions = [
  {
    key: "academy",
    to: "/apply",
    icon: GraduationCap,
    title: "Start your Academy application",
    body: "Apply to a training program, earn an industry credential, and get career support for life.",
    cta: "Begin application",
  },
  {
    key: "talent",
    to: "/talent-signup",
    icon: Database,
    title: "Share your resume for our Talent Portal",
    body: "Not ready to enroll? Get your profile in front of vetted employers — free, no application.",
    cta: "Create Talent profile",
  },
  {
    key: "community",
    to: "/join-community",
    icon: Users,
    title: "Connect with the community",
    body: "Join the Aspiritech community, alumni network, and success-story circle. Meet people who get it.",
    cta: "Join the community",
  },
];

const SignupChooser = () => {
  const [path, setPath] = useState(null); // null | "jobseeker"

  return (
    <div className="min-h-screen flex flex-col bg-surface-app" data-testid="signup-chooser-page">
      <Header />
      <main className="flex-1">
        <section className="bg-gradient-to-br from-[#0F1B3D] via-[#1E3A8A] to-[#0A1A4A] text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-[420px] h-[420px] bg-[#FFD507]/10 rounded-full blur-3xl pointer-events-none" />
          <div className="max-w-4xl mx-auto px-6 py-14 relative">
            <p className="text-[#FFD507] text-xs uppercase tracking-[0.25em] font-bold mb-3">Join Aspiritech</p>
            <h1 className="text-3xl sm:text-5xl font-heading font-bold leading-tight">
              How would you like to get started?
            </h1>
            <p className="text-slate-300 mt-4 max-w-2xl">
              Choose the path that fits you. You can always explore the others later.
            </p>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-6 py-12">
          {path === null ? (
            <div className="grid md:grid-cols-2 gap-6" data-testid="role-choice">
              <Card className="border border-slate-200 hover:border-[#1E3A8A] transition-colors">
                <CardContent className="p-8 flex flex-col h-full">
                  <div className="w-14 h-14 rounded-2xl bg-[#1E3A8A]/10 flex items-center justify-center mb-5">
                    <GraduationCap className="w-7 h-7 text-[#1E3A8A]" />
                  </div>
                  <h2 className="text-2xl font-heading font-bold text-slate-900">I'm a Jobseeker</h2>
                  <p className="text-slate-600 mt-2 flex-1">
                    Autistic or neurodivergent adult looking to build a tech career, share your resume,
                    or join our community.
                  </p>
                  <Button
                    className="btn-primary mt-6 w-full h-11"
                    onClick={() => setPath("jobseeker")}
                    data-testid="choose-jobseeker-btn"
                  >
                    Continue as Jobseeker <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>

              <Card className="border border-slate-200 hover:border-[#FFD507] transition-colors">
                <CardContent className="p-8 flex flex-col h-full">
                  <div className="w-14 h-14 rounded-2xl bg-[#FFD507]/20 flex items-center justify-center mb-5">
                    <Heart className="w-7 h-7 text-[#0F1B3D]" />
                  </div>
                  <h2 className="text-2xl font-heading font-bold text-slate-900">I want to Mentor</h2>
                  <p className="text-slate-600 mt-2 flex-1">
                    Industry professional ready to guide neurodivergent talent. A few hours a month makes
                    a lasting impact.
                  </p>
                  <Link to="/mentor-signup" className="mt-6" data-testid="choose-mentor-link">
                    <Button className="btn-primary w-full h-11">
                      Continue as Mentor <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                  <Link
                    to="/mentor-guide"
                    className="text-sm text-[#1E3A8A] hover:underline mt-3 inline-flex items-center gap-1"
                    data-testid="signup-mentor-guide-link"
                  >
                    <FileText className="w-3.5 h-3.5" /> First, see what's expected of a mentor
                  </Link>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div data-testid="jobseeker-options">
              <button
                onClick={() => setPath(null)}
                className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#1E3A8A] mb-6"
                data-testid="jobseeker-back-btn"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-1">Pick your jobseeker path</h2>
              <p className="text-slate-600 mb-6">Three ways to get started — choose what feels right today.</p>
              <div className="grid md:grid-cols-3 gap-5">
                {jobseekerOptions.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <Card key={opt.key} className="border border-slate-200 hover:shadow-md transition-shadow flex flex-col">
                      <CardContent className="p-6 flex flex-col h-full">
                        <div className="w-12 h-12 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center mb-4">
                          <Icon className="w-6 h-6 text-[#1E3A8A]" />
                        </div>
                        <h3 className="font-heading font-bold text-slate-900 text-lg">{opt.title}</h3>
                        <p className="text-sm text-slate-600 mt-2 flex-1">{opt.body}</p>
                        <Link to={opt.to} className="mt-5" data-testid={`jobseeker-option-${opt.key}`}>
                          <Button className="btn-primary w-full">
                            {opt.cta} <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              <div className="mt-8 flex items-start gap-2 text-sm text-slate-500">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" />
                Not sure? Start with the Talent Portal — it's free and you can apply to the Academy anytime.
              </div>
            </div>
          )}

          <p className="text-sm text-slate-500 text-center mt-10">
            Already have an account?{" "}
            <Link to="/login" className="text-[#1E3A8A] hover:underline font-medium" data-testid="signup-login-link">
              Sign in instead
            </Link>
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default SignupChooser;
